import type {ReactElement} from 'react';
import React, {useRef, useState} from 'react';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {Dropdown} from '@Components/dropdown-v2';
import {ControlledListItemType7} from '@Components/controlled-list/components/controlled-list-item-type-7';
import styles from './nav-settings-option.module.scss';
import {Text, TextSize} from '@Components/text';
import {ToggleSwitch, ToggleSwitchV2Size} from '@Components/toggle-switch-v2';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {useAppSelector} from '@/hooks';
import {updateAreAlignmentGuidesEnabled, updateIsAutosaveEnabledForUser, updateIsMultiplePagesEnabledForUser} from '@/reducers/user-preferences.reducer';

export function NavSettingsOption(): ReactElement {
  const isAutoSaveEnabled = useAppSelector((state) => {
    return state.userPreferences.isAutosaveEnabled;
  });
  const isMultiPageEnabled = useAppSelector((state) => {
    return state.userPreferences.isMultiPageEnabled;
  });
  const isMultiPagePreferenceUpdating = useAppSelector((state) => {
    return state.userPreferences.isMultiPagePreferenceUpdating;
  });
  const isAutosavePreferenceUpdating = useAppSelector((state) => {
    return state.userPreferences.isAutosavePreferenceUpdating;
  });
  const areAlignmentGuidesEnabled = useAppSelector((state) => {
    return state.userPreferences.areAlignmentGuidesEnabled;
  });

  const areAlignmentGuidesPreferenceUpdating = useAppSelector((state) => {
    return state.userPreferences.areAlignmentGuidesPreferenceUpdating;
  });

  const [isHovered, setIsHovered] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const onMouseEnter = (): void => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setTimeout((): void => {
      setIsHovered(true);
    }, 300);
  };

  const onMouseExit = (): void => {
    timeoutRef.current = setTimeout((): void => {
      setIsHovered(false);
    }, 300);
  };

  const getSettingsDropdownSelector = (): ReactElement<any> => {
    return (
      <div onMouseEnter={onMouseEnter} onMouseLeave={onMouseExit}>
        <ControlledListItemType7
          rightIconClassName={styles.rightIconColor}
          rightIcon="icon-caret-right"
          className="spacing-m-b-2"
          id="language-dropdown"
          text={window.i18next.t('pmwjs_settings')}
          icon="icon-settings"
        />
      </div>
    );
  };

  const onToggleAligned = (): void => {
    void updateAreAlignmentGuidesEnabled(!areAlignmentGuidesEnabled);
  };

  const onToggleAutosave = (): void => {
    void updateIsAutosaveEnabledForUser(!isAutoSaveEnabled);
  };

  const onToggleMultiPages = (): void => {
    void updateIsMultiplePagesEnabledForUser(!isMultiPageEnabled);
  };

  const getSettingsList = (): ReactElement => {
    return (
      <ul className="content-body spacing-m-0">
        <li className="flex-justify-between flex-items-center spacing-m-b-4 spacing-p-b-0">
          <Text size={TextSize.XSMALL} val={window.i18next.t('pmwjs_alignment_guides')} />
          <div onClick={onToggleAligned}>
            <ToggleSwitch size={ToggleSwitchV2Size.MEDIUM} isChecked={areAlignmentGuidesEnabled} isLoading={areAlignmentGuidesPreferenceUpdating} onChange={onToggleAligned} />
          </div>
        </li>
        {window.posterEditor?.whiteboard?.savePoster.isUserIncludedInAutoSaveRollout() ? (
          <li className="flex-justify-between flex-items-center spacing-m-b-4 spacing-p-b-0">
            <Text size={TextSize.XSMALL} val={window.i18next.t('pmwjs_autosave')} />
            <div onClick={onToggleAutosave}>
              <ToggleSwitch size={ToggleSwitchV2Size.MEDIUM} isChecked={isAutoSaveEnabled} isLoading={isAutosavePreferenceUpdating} onChange={onToggleAutosave} />
            </div>
          </li>
        ) : null}
        {window.posterEditor?.config?.isMultiPageRolledOutForUser ? (
          <li className="flex-justify-between flex-items-center spacing-m-b-4 spacing-p-b-0">
            <Text size={TextSize.XSMALL} val={window.i18next.t('pmwjs_multiple_pages')} />
            <div onClick={onToggleMultiPages}>
              <ToggleSwitch size={ToggleSwitchV2Size.MEDIUM} isChecked={isMultiPageEnabled} isLoading={isMultiPagePreferenceUpdating} onChange={onToggleMultiPages} />
            </div>
          </li>
        ) : null}
        <li className="flexbox flex-row-justify-between flex-items-center spacing-p-b-0">
          <Text size={TextSize.XSMALL} val={window.i18next.t('pmwjs_accessibility')} />
          <Button
            onClick={(): void => {
              window.PMW.initAccessibilityMenu();
            }}
            size={Size.XSMALL}
            type={Type.SECONDARY}
            iconPlacement={IconPlacement.LEFT}
            text="View Options"
            icon="icon-wheelchair"
          />
        </li>
      </ul>
    );
  };

  const onClickSettingsOptions = (e: React.MouseEvent<HTMLElement>): void => {
    e.stopPropagation();
    e.preventDefault();
  };

  return (
    <div onClick={onClickSettingsOptions}>
      <Dropdown
        open={isHovered}
        leftOffset={8}
        popupSpacingClass="spacing-p-4"
        position={DROPDOWN_POSITION.FIXED}
        alignment={ALIGNMENT_TYPE.RIGHT_START}
        selector={getSettingsDropdownSelector()}
        popupClassName={`${styles.settingsPopup} flex-center`}
        popUpHasCustomWidth
        popUpHasCustomHeight
        stopClickPropagation
        doCloseOnInnerClick={false}
        doesCloseOnOuterClick={false}
        popup={
          <div className="_full-width _full-height" onMouseEnter={onMouseEnter} onMouseLeave={onMouseExit}>
            {getSettingsList()}
          </div>
        }
      />
    </div>
  );
}
